import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

// Same-origin by default: FastAPI serves both /api/* and this SPA from one origin
// (see Dockerfile). Falling back to "" yields a relative /api, which is correct in
// production and avoids `undefined/api` when the env var is unset.
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function FeedbackForm() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, done: false, gone: false, event: null, error: null });
  const [nps, setNps] = useState(null);
  const [q1, setQ1] = useState("");
  const [q2, setQ2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/feedback/${token}`);
        setState({ loading: false, done: false, gone: false, event: r.data.event, name: r.data.name });
      } catch (e) {
        const status = e.response?.status;
        if (status === 410) setState({ loading: false, done: true, gone: true });
        else setState({ loading: false, error: e.response?.data?.detail || "Link not found" });
      }
    })();
  }, [token]);

  const submit = async () => {
    if (nps === null) return toast.error("Please pick a score from 0 to 10");
    setBusy(true);
    try {
      await axios.post(`${API}/feedback/${token}`, { nps, q1_worked: q1, q2_improve: q2 });
      setState((s) => ({ ...s, done: true }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
      setBusy(false);
    }
  };

  if (state.loading) return <PageShell><div className="text-sm text-muted-foreground">Loading…</div></PageShell>;
  if (state.error) return <PageShell><h1 className="font-heading text-2xl">Feedback link not found</h1><p className="text-sm text-muted-foreground mt-2">This link may have expired or been mistyped.</p></PageShell>;
  if (state.done) return (
    <PageShell>
      <div className="mx-auto mb-4 h-16 w-16 rounded-full flex items-center justify-center" style={{ background: "hsl(var(--vd-green) / 0.15)", color: "hsl(var(--vd-green))" }}>
        <Check size={28} strokeWidth={2.5} />
      </div>
      <h1 className="font-heading text-2xl">{state.gone ? "You've already shared feedback" : "Thanks, this helps us make these better."}</h1>
      <p className="text-sm text-muted-foreground mt-2">You can close this tab.</p>
    </PageShell>
  );

  const npsColour = (n) => (nps === n ? { background: "hsl(var(--vd-green))", color: "#fff", borderColor: "hsl(var(--vd-green))" } : {});

  return (
    <PageShell wide>
      <div className="overline">Post-event feedback</div>
      <h1 className="font-heading text-3xl leading-tight mt-1" data-testid="feedback-event-title">{state.event?.title}</h1>
      <p className="text-sm text-muted-foreground mt-2">
        {state.name ? `Hi ${state.name.split(" ")[0]}, ` : ""}three quick questions — takes about 30 seconds.
      </p>

      <div className="mt-8">
        <div className="text-sm font-medium">How likely are you to recommend Venture Dock events to a founder?</div>
        <div className="text-xs text-muted-foreground mt-1 font-mono">0 = not at all likely · 10 = extremely likely</div>
        <div className="mt-4 grid grid-cols-11 gap-1.5" role="radiogroup" aria-label="NPS score">
          {Array.from({ length: 11 }, (_, i) => i).map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => setNps(i)}
              className="h-11 rounded-lg border border-border text-sm font-mono transition-colors"
              style={npsColour(i)}
              aria-pressed={nps === i}
              data-testid={`nps-${i}`}
            >
              {i}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <label className="text-sm font-medium">What worked well?</label>
        <textarea rows={3} className="input-field resize-none mt-2" value={q1} onChange={(e) => setQ1(e.target.value)} placeholder="Optional — tell us what stood out" data-testid="feedback-q1" />
      </div>

      <div className="mt-6">
        <label className="text-sm font-medium">What could be better?</label>
        <textarea rows={3} className="input-field resize-none mt-2" value={q2} onChange={(e) => setQ2(e.target.value)} placeholder="Optional — what would you change?" data-testid="feedback-q2" />
      </div>

      <div className="mt-8 flex justify-end">
        <button onClick={submit} disabled={busy || nps === null} className="btn-primary" data-testid="feedback-submit">
          <Send size={13} />{busy ? "Sending…" : "Submit feedback"}
        </button>
      </div>
    </PageShell>
  );
}

function PageShell({ children, wide }) {
  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-16" style={{ background: "hsl(var(--background))" }}>
      <div className={`glass-panel w-full ${wide ? "max-w-xl" : "max-w-md"} p-8 md:p-10 text-center relative`}>
        {children}
      </div>
    </div>
  );
}
